/**
 * LegalPage Component
 *
 * Comprehensive legal disclaimer page covering Terms of Service,
 * Limitation of Liability, Privacy Policy, AI & Content Disclaimer,
 * Indemnification, and Recording Consent.
 */

import { Link } from "react-router-dom";
import { HeartHandshake } from "lucide-react";

export function LegalPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <HeartHandshake className="w-6 h-6 text-primary" />
            <span className="text-xl font-bold text-foreground">
              AttuneWell
            </span>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <h1 className="text-4xl font-bold text-foreground mb-2">
          Legal & Policies
        </h1>
        <p className="text-muted-foreground mb-12">
          Last updated: February 2026
        </p>

        {/* Terms of Service */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            Terms of Service
          </h2>
          <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              By accessing or using AttuneWell ("the Service"), you agree to be
              bound by these Terms of Service. If you do not agree to these
              terms, please do not use the Service.
            </p>
            <h3 className="text-base font-medium text-foreground">
              Account Responsibility
            </h3>
            <p>
              You are responsible for maintaining the confidentiality of your
              account credentials and for all activities that occur under your
              account. You must provide accurate and complete information when
              creating an account and keep your information up to date.
            </p>
            <h3 className="text-base font-medium text-foreground">
              Acceptable Use
            </h3>
            <p>You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Upload content that you do not have the right to share</li>
              <li>
                Harass, abuse, or harm others or use the Service in connection
                with any unlawful activity
              </li>
              <li>
                Attempt to reverse-engineer, disassemble, or otherwise interfere
                with the Service
              </li>
              <li>
                Use the Service as a substitute for professional mental health
                treatment when such treatment is needed
              </li>
            </ul>
            <h3 className="text-base font-medium text-foreground">
              Modifications
            </h3>
            <p>
              We reserve the right to modify these terms at any time. Continued
              use of the Service after changes constitutes acceptance of the
              updated terms.
            </p>
          </div>
        </section>

        {/* Disclaimer / Limitation of Liability */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            Disclaimer & Limitation of Liability
          </h2>
          <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p className="font-medium text-foreground">
              AttuneWell is NOT a substitute for professional therapy, couples
              counseling, or mental health treatment.
            </p>
            <p>
              The Service provides AI-generated analysis and insights for
              educational and informational purposes only. No professional
              therapeutic relationship is created between you and AttuneWell.
            </p>
            <p>
              AttuneWell, its creators, employees, and affiliates shall not be
              held liable for any decisions made, actions taken, or outcomes
              resulting from the use of this Service. This includes, but is not
              limited to, any relationship outcomes, emotional distress, or
              personal decisions influenced by the AI-generated content.
            </p>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
              WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT
              NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              IN NO EVENT SHALL ATTUNEWELL BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING
              OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
            </p>
          </div>
        </section>

        {/* Privacy Policy */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            Privacy Policy
          </h2>
          <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <h3 className="text-base font-medium text-foreground">
              Data Collection
            </h3>
            <p>
              We collect information you provide directly, including account
              details (email, name), uploaded audio recordings, conversation
              transcriptions, relationship tracker entries, and chat
              interactions with our AI.
            </p>
            <h3 className="text-base font-medium text-foreground">
              Data Storage & Security
            </h3>
            <p>
              All data is encrypted at rest and in transit. Audio recordings and
              transcriptions are stored in encrypted cloud storage. We employ
              industry-standard security measures to protect your information.
            </p>
            <h3 className="text-base font-medium text-foreground">
              AI Processing
            </h3>
            <p>
              Your conversation data is processed by third-party AI services
              (large language models) to generate analysis and insights. This
              processing is done under strict data processing agreements, and
              your data is not used to train AI models.
            </p>
            <h3 className="text-base font-medium text-foreground">
              No Data Sharing
            </h3>
            <p>
              We do not sell, rent, or share your personal data or conversation
              content with third parties for marketing or advertising purposes.
              Data may be shared only as required by law or as necessary to
              provide the Service (e.g., cloud hosting, payment processing).
            </p>
            <h3 className="text-base font-medium text-foreground">
              Data Deletion
            </h3>
            <p>
              You have the right to request deletion of your data at any time.
              Upon account deletion, all associated recordings, transcriptions,
              analyses, and personal data will be permanently removed from our
              systems within 30 days.
            </p>
          </div>
        </section>

        {/* AI & Content Disclaimer */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            AI & Content Disclaimer
          </h2>
          <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              AttuneWell uses large language models (LLMs) and other AI
              technologies to analyze conversations and generate insights. You
              should be aware of the following:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-foreground">
                  AI outputs may be inaccurate.
                </strong>{" "}
                LLMs can produce errors, misinterpretations, or
                "hallucinations"—content that sounds plausible but is factually
                incorrect.
              </li>
              <li>
                <strong className="text-foreground">
                  AttuneWell is not a licensed therapist.
                </strong>{" "}
                The AI does not hold any professional licenses, certifications,
                or qualifications in psychology, counseling, or any mental
                health field.
              </li>
              <li>
                <strong className="text-foreground">
                  For educational and informational purposes only.
                </strong>{" "}
                The insights provided are meant to encourage self-reflection and
                communication awareness, not to diagnose or treat any mental
                health condition.
              </li>
              <li>
                <strong className="text-foreground">
                  Seek professional help for serious issues.
                </strong>{" "}
                If you or your partner are experiencing domestic violence,
                severe mental health challenges, substance abuse, or suicidal
                thoughts, please contact a licensed professional or crisis
                hotline immediately.
              </li>
            </ul>
          </div>
        </section>

        {/* Indemnification */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            Indemnification
          </h2>
          <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              By using this Service, you agree to indemnify and hold harmless
              AttuneWell, its officers, directors, employees, agents, and
              affiliates from any claims, damages, losses, or expenses
              (including legal fees) arising out of or related to:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your use of the Service</li>
              <li>Any decisions made based on AI-generated content</li>
              <li>Any relationship outcomes influenced by the Service</li>
              <li>Your violation of these terms or any applicable law</li>
            </ul>
          </div>
        </section>

        {/* Recording Consent */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            Recording Consent
          </h2>
          <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p className="font-medium text-foreground">
              You are solely responsible for ensuring that all parties in a
              recorded conversation have given their consent to being recorded.
            </p>
            <p>
              Many jurisdictions have "two-party consent" (or "all-party
              consent") laws that require every participant in a conversation to
              agree to being recorded. Violating these laws can result in
              criminal charges and civil liability.
            </p>
            <p>
              AttuneWell does not verify recording consent and assumes no
              liability for recordings uploaded without proper consent. By
              uploading a recording, you represent and warrant that you have
              obtained all necessary consent from all participants.
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/30 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <HeartHandshake className="w-5 h-5 text-primary" />
              <span className="font-bold text-foreground">AttuneWell</span>
            </Link>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <Link
                to="/legal"
                className="hover:text-foreground transition-colors"
              >
                Legal
              </Link>
              <span>|</span>
              <span>&copy; 2026 AttuneWell. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
